import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ToastHost } from './shell/Toast';
import { apiGet, ApiError } from './api';
import { Gallery } from './gallery/Gallery';
import { Activate } from './pages/Activate';
import { LoginPage, RegisterPage } from './auth/AuthPages';
import { Home } from './pages/Home';
import { Dividends } from './pages/Dividends';
import { Equity } from './pages/Equity';
import { Settings } from './pages/Settings';
import { Tax } from './pages/Tax';
import { Admin } from './pages/Admin';
import { AdminMetrics, AdminTests, AdminJobs } from './pages/admin/AdminPanels';
import { ListPage } from './pages/ListPage';
import { ErrorBoundary } from './shell/ErrorBoundary';
import { Header } from './shell/Header';
import { FEEDBACK_WIDGET_EVENT, FeedbackWidget } from './shell/FeedbackWidget';
import type { FeedbackVariant } from './shell/FeedbackWidget';

type WhoAmI = { id: number; email: string; role: string; theme: string; logo_animate?: number | boolean };

/**
 * The session the shell was SERVED with, injected by spa-server as window.__BOOT__.
 *
 * Without it the page cannot paint anything until whoami answers — a second round trip after the bundle —
 * and the theme is applied after the first paint rather than before it (a light flash then the real theme).
 * Measured: 148ms LCP against the EJS page's 116ms, almost all of it that extra trip.
 */
type Boot = { user: WhoAmI | null; appVersion: string; dev: boolean; feedbackWidget?: string };
const boot = (window as unknown as { __BOOT__?: Boot }).__BOOT__;

type Health = { app: string; version: string; spa: boolean; dev: boolean };

function Shell() {
  const me = useQuery<WhoAmI, ApiError>({
    queryKey: ['whoami'],
    queryFn: () => apiGet<WhoAmI>('/api/v1/whoami'),
    // The boot payload IS the answer to this query, so the first render is already authenticated. `staleTime`
    // keeps it from being refetched immediately; the query still owns the error path (a 401 after the session
    // expires, which the boot payload cannot know about).
    initialData: boot?.user ?? undefined,
    staleTime: 60_000,
  });
  const health = useQuery<Health, ApiError>({
    queryKey: ['health'],
    queryFn: () => apiGet<Health>('/api/v1/health'),
    initialData: boot ? { app: 'portfoolio', version: boot.appVersion, spa: true, dev: boot.dev } : undefined,
    staleTime: 60_000,
  });
  const { pathname } = useLocation();
  // The launcher's style is a site setting (`feedback_widget`), baked into the boot payload exactly as the EJS
  // layout reads it. It is held here rather than in the widget because the admin page changes it while the app is
  // running: that page dispatches FEEDBACK_WIDGET_EVENT and the shell redraws the launcher on the spot.
  const [fbVariant, setFbVariant] = useState<FeedbackVariant>((boot?.feedbackWidget as FeedbackVariant) || 'bubble');
  useEffect(() => {
    const onChange = (e: Event) => {
      const next = (e as CustomEvent).detail as FeedbackVariant;
      if (next === 'bubble' || next === 'tab' || next === 'none') setFbVariant(next);
    };
    window.addEventListener(FEEDBACK_WIDGET_EVENT, onChange);
    return () => window.removeEventListener(FEEDBACK_WIDGET_EVENT, onChange);
  }, []);
  // The class the stylesheet positions the bubble with (views/layout.ejs writes it on <body>).
  useEffect(() => {
    document.body.classList.toggle('fb-bubble-on', fbVariant === 'bubble');
  }, [fbVariant]);

  // Hooks first, returns after: a hook below an early return is React error #310 (rendered fewer hooks
  // than expected) — which is exactly how this page rendered blank the first time it was built.
  const theme = me.data?.theme;
  useEffect(() => {
    if (theme) document.documentElement.dataset.theme = theme;
  }, [theme]);
  const needsLogin = me.error?.status === 401;

  if (me.isLoading) return <main style={{ padding: 24 }}>Loading…</main>;
  if (needsLogin) {
    // The SPA's own auth pages: no EJS, no redirect out of the app. Anything that is not /login or
    // /register bounces to /login, which is what requireAuth did on the server.
    return (
      <Routes>
        <Route path="/login" element={<LoginPage next={loginNext()} />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }
  if (me.error) return <main style={{ padding: 24 }}>Could not reach the API: {me.error.message}</main>;

  const user = me.data!;

  // views/activate.ejs renders without the app header — a focused task page, one card, nothing else. It is
  // still behind auth (the EJS route redirects to /login with a next=), which the needsLogin branch above
  // already does for any path.
  if (pathname === '/activate') return <Activate />;

  return (
    <>
      {/* No extra nav entry for /gallery: the nav is the EJS nav, item for item, and a fifth link pushed
          the account pill past the right edge at 780px. A dev instrument is reachable by URL. */}
      <Header user={user} path={pathname} dev={health.data?.dev} />
      {/* The shell's own feedback control: it belongs to every page, because the page you were on when
          something went wrong is the context that makes the report useful. */}
      <FeedbackWidget path={pathname} variant={fbVariant} />
      <ErrorBoundary>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/dividends" element={<Dividends />} />
          <Route path="/equity" element={<Equity />} />
          <Route path="/settings/:tab" element={<SettingsRoute />} />
          <Route path="/settings" element={<SettingsRoute />} />
          <Route path="/tax" element={<Tax />} />
          {/* The admin tabs are real URLs (the EJS bar is link mode with hrefs), so each tab is a route and the
              URL is the tab state — deep-linkable, reload-stable, and visible in the request metrics. */}
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/overview" element={<Admin />} />
          <Route path="/admin/metrics" element={<AdminMetrics />} />
          <Route path="/admin/tests" element={<AdminTests />} />
          <Route path="/admin/jobs" element={<AdminJobs />} />
          <Route path="/admin/storybook" element={<Gallery />} />
          <Route path="/lists/:id" element={<ListRoute />} />
          {/* The EJS server redirects both of these (app.get('/lists') -> '/', and the tokens reference ->
              /settings/api). Without the redirects here they fall through to the placeholder, so an old link
              reads as an unported page instead of the page it has always opened. */}
          <Route path="/lists" element={<Navigate to="/" replace />} />
          <Route path="/settings/tokens/reference" element={<Navigate to="/settings/api" replace />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="*" element={<Placeholder health={health.data} user={user} />} />
        </Routes>
      </ErrorBoundary>
      {/* The layout's confirmation strip, after the page — `<div class="toast" id="toast">` in views/layout.ejs.
          A save says "✓ Saved" through it, which is how the EJS page confirms and how the SPA said nothing. */}
      <ToastHost />
    </>
  );
}

/** The pages arrive in P3; until then each route says so instead of rendering an empty shell. */
function Placeholder({ health, user }: { health?: Health; user: WhoAmI }) {
  const { pathname } = useLocation();
  return (
    <main>
      <section className="panel-card">
        <h2 className="t-title" style={{ marginTop: 0 }}>
          {pathname === '/' ? 'The SPA shell is up' : `${pathname} is not ported yet`}
        </h2>
        <p className="muted t-body">
          Signed in as <strong>{user.email}</strong> ({user.role}), theme <code>{user.theme}</code>. The design
          system is ported and lives at <a href="/gallery">/gallery</a>; pages follow in P2/P3. Until then the
          EJS app on :9231 remains the app you use.
        </p>
        <p className="muted t-small">
          API: <code>{health ? `${health.app} ${health.version}` : '…'}</code>
          {health?.spa ? ' · serving the built SPA' : ''}
        </p>
      </section>
    </main>
  );
}

// Where the user was trying to go, so signing in lands them there. Only a local path is passed on —
// the server re-checks it (services/auth.js: safeNext), and this keeps the client from even offering
// a redirect target taken from the URL.
function loginNext(): string | undefined {
  const here = window.location.pathname + window.location.search;
  return here && here !== '/' && !here.startsWith('/login') ? here : undefined;
}

// The list page takes its id from the URL, the same URL the EJS app uses.
function ListRoute() {
  const { id } = useParams();
  const n = Number(id);
  return Number.isFinite(n) ? <ListPage id={n} key={n} /> : <main className="panel-card">Bad list id.</main>;
}

// Settings picks its panel from the URL, so a link to /settings/tokens opens the token list — the same
// URLs the EJS page has.
function SettingsRoute() {
  const { tab } = useParams();
  // /settings is the Profile tab, and the URL SAYS so: the EJS page redirects, so a bookmark of /settings and a
  // bookmark of /settings/profile are the same page with the same address.
  if (!tab) return <Navigate to="/settings/profile" replace />;
  return <Settings tab={tab} key={tab} />;
}

export default function App() {
  return <Shell />;
}
